import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { PageTitle, Meta, Display, Chip, Button } from '../components/Atoms';
import Layout, { useViewport, TopBar } from '../components/Layout';
import { LoadingState, ErrorState, EmptyState } from '../components/states';
import { useApi, plantStage, daysSince, formatDate, usePlotRotation } from '../lib/api';
import { Sprout } from '../components/Illustrations';
import {
  PlotFormModal, PlotDeleteModal,
  BedFormModal, BedDuplicateModal, BedHarvestModal, BedDeleteModal, BedPhotosModal,
} from './plot/PlotModals';

/**
 * Plot screen - lista działek + szczegóły wybranej.
 * Z REAL DATA z konta zalogowanego.
 *
 * Routy:
 *   /plots         - lista działek + auto-select pierwszej
 *   /plots/:id     - konkretna działka (deep link)
 *
 * Tabs (z 04-component-composition.md ekran 2):
 *   Plan | Grządki | Plan 2027 | Kwitnienie | Zimowanie
 *   (Plan + Grządki — wystarczające dla wszystkich akcji CRUD)
 */

const PLOT_COLORS = ['var(--t-sage)', 'var(--t-rust)', 'var(--t-amber)', 'var(--t-sage-deep)'];
const PLOT_COLORS_RAW = ['#a8c49e', '#c06a4a', '#dcb464', '#7e9b74'];

function bedColor(idx) {
  return PLOT_COLORS[idx % PLOT_COLORS.length];
}
function bedColorRaw(idx) {
  return PLOT_COLORS_RAW[idx % PLOT_COLORS_RAW.length];
}

// ── Bed card w canvasie (top-down view) ──────────────────────────
function Bed({ bed, idx, selected, onClick }) {
  const color = bedColor(idx);
  const colorRaw = bedColorRaw(idx);
  const stage = plantStage(bed);
  const letter = String.fromCharCode(65 + idx); // A, B, C...

  // Zdjęcie grządki — fallback chain (backend już ustawia display_image):
  //   1. własne zdjęcie usera (bed.image_path)
  //   2. zdjęcie rośliny z bazy katalogu (gdy user posadził Pomidor → bierzemy nasze zdjęcie)
  //   3. ilustracja z katalogu
  //   4. brak (gradient)
  const imageRaw = bed.display_image || bed.image_path || bed.latest_photo_path || bed.thumb_path;
  const imageUrl = imageRaw
    ? (imageRaw.startsWith('http') || imageRaw.startsWith('/') ? imageRaw : '/' + imageRaw)
    : null;
  const isOwnPhoto = Boolean(bed.image_is_own);

  return (
    <div
      onClick={onClick}
      style={{
        aspectRatio: '1.3/1',
        borderRadius: 10,
        background: imageUrl
          ? `linear-gradient(180deg, rgba(15,20,16,.0) 0%, rgba(15,20,16,.35) 55%, rgba(15,20,16,.85) 100%), url(${imageUrl}) center / cover`
          : `linear-gradient(135deg, ${colorRaw}30, ${colorRaw}0d)`,
        border: selected ? `2px solid ${colorRaw}` : '1px solid var(--t-line-2)',
        padding: '10px 12px',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        position: 'relative', cursor: 'pointer',
        transition: 'transform 200ms ease',
        overflow: 'hidden',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        position: 'relative', zIndex: 1,
      }}>
        <Display size={24} style={{
          color: imageUrl ? '#fff' : color, fontWeight: 400, lineHeight: 1,
          textShadow: imageUrl ? '0 1px 2px rgba(0,0,0,0.6)' : 'none',
        }}>{letter}</Display>
        <Meta style={{
          fontSize: 8,
          color: imageUrl ? 'rgba(255,255,255,0.85)' : undefined,
          textShadow: imageUrl ? '0 1px 2px rgba(0,0,0,0.6)' : 'none',
        }}>rząd {bed.row_number || idx + 1}</Meta>
      </div>
      <div style={{ position: 'relative', zIndex: 1 }}>
        <Display size={14} style={{
          display: 'block', lineHeight: 1.1, letterSpacing: '-.01em',
          textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap',
          color: imageUrl ? '#fff' : 'var(--t-text)',
          textShadow: imageUrl ? '0 1px 3px rgba(0,0,0,0.7)' : 'none',
        }}>{bed.plant_name || 'Pusty'}</Display>
        {bed.plant_variety && (
          <div style={{
            fontSize: 10, marginTop: 2,
            color: imageUrl ? 'rgba(255,255,255,0.85)' : 'var(--t-text-dim)',
            textShadow: imageUrl ? '0 1px 2px rgba(0,0,0,0.7)' : 'none',
            textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap',
          }}>{bed.plant_variety}</div>
        )}
      </div>
      {/* Status dot - zdrowie zagonu w jednym rzucie */}
      <div style={{
        position: 'absolute', top: 8, right: 8, zIndex: 2,
        width: 7, height: 7, borderRadius: '50%',
        background: stage.tone === 'rust' ? 'var(--t-rust)' :
                    stage.tone === 'amber' ? 'var(--t-amber)' :
                    'var(--t-sage)',
        boxShadow: imageUrl ? '0 0 0 2px rgba(15,20,16,0.7)' : 'none',
      }} />

      {/* Subtelny indicator że zdjęcie jest z bazy katalogu, nie własne usera */}
      {imageUrl && !isOwnPhoto && (
        <div style={{
          position: 'absolute', bottom: 8, right: 8, zIndex: 2,
          padding: '2px 6px', borderRadius: 4,
          background: 'rgba(15, 20, 16, 0.7)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          fontFamily: 'var(--t-mono)', fontSize: 8,
          letterSpacing: '.18em', textTransform: 'uppercase',
          color: 'rgba(255, 255, 255, 0.7)',
        }} title="Zdjęcie z katalogu — wgraj własne aby je zastąpić">KATALOG</div>
      )}
    </div>
  );
}

// ── Plot canvas - siatka grządek z ramką "ogrodzenia" ────────────
function PlotCanvas({ beds, selectedId, onSelect, big }) {
  const padding = big ? 28 : 20;
  const cols = beds.length <= 2 ? beds.length : 2;
  const rows = Math.ceil(beds.length / cols);

  return (
    <div style={{
      padding,
      borderRadius: 18,
      background: 'var(--t-bg-elev)',
      border: '1px solid var(--t-line)',
      position: 'relative',
    }}>
      <div style={{
        position: 'absolute', top: 16, right: 20,
        fontFamily: 'var(--t-mono)', fontSize: 10,
        letterSpacing: '.2em', color: 'var(--t-text-mute)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 14, color: 'var(--t-amber)' }}>↑</div>N
      </div>

      {beds.length === 0 ? (
        <EmptyState
          illustration="bed"
          title="Pusta działka"
          description="Gleba czeka na pierwszą grządkę. Zacznij od jednego rzędu — reszta pójdzie sama."
        />
      ) : (
        <div style={{
          border: '1.5px dashed var(--t-line-2)',
          borderRadius: 8, padding: 14,
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, auto)`,
          gap: 10,
        }}>
          {beds.map((bed, idx) => (
            <Bed
              key={bed.id}
              bed={bed}
              idx={idx}
              selected={selectedId === bed.id}
              onClick={() => onSelect(bed.id)}
            />
          ))}
        </div>
      )}

      <div style={{
        marginTop: 14, textAlign: 'center',
        fontFamily: 'var(--t-mono)', fontSize: 10,
        letterSpacing: '.3em', color: 'var(--t-text-mute)',
      }}>
        ──── OGRODZENIE ────
      </div>
    </div>
  );
}

// ── Lista (alternatywa dla canvasu) ──────────────────────────────
function BedList({ beds, selectedId, onSelect }) {
  if (beds.length === 0) {
    return (
      <EmptyState
        illustration="bed"
        title="Pusta działka"
        description="Dodaj pierwszą grządkę — kolekcja zaczyna się od jednej rośliny."
      />
    );
  }
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 1,
      background: 'var(--t-line)', borderRadius: 14, overflow: 'hidden',
    }}>
      {beds.map((bed, idx) => {
        const color = bedColor(idx);
        const stage = plantStage(bed);
        const letter = String.fromCharCode(65 + idx);
        return (
          <div
            key={bed.id}
            onClick={() => onSelect(bed.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '14px 16px',
              background: selectedId === bed.id ? 'rgba(168, 196, 158, 0.06)' : 'var(--t-bg-elev)',
              cursor: 'pointer',
            }}
          >
            <Display size={20} style={{ width: 28, color, fontWeight: 400 }}>{letter}</Display>
            <div style={{ width: 4, height: 36, borderRadius: 2, background: color, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Display size={16} style={{ display: 'block' }}>{bed.plant_name || 'Pusty'}</Display>
              <div style={{ fontSize: 11, color: 'var(--t-text-dim)', marginTop: 2 }}>
                {[bed.plant_variety, `rząd ${bed.row_number || idx + 1}`].filter(Boolean).join(' · ')}
              </div>
            </div>
            {stage.d != null && (
              <div style={{ textAlign: 'right' }}>
                <Display size={16} style={{ color }}>d{stage.d}</Display>
                <Meta color={stage.tone}>{stage.stage}</Meta>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Mała ikonka SVG dla akcji bedu ──────────────────────────────
function ActionIcon({ name, color = 'currentColor', size = 14 }) {
  const props = { width: size, height: size, viewBox: '0 0 16 16', fill: 'none' };
  switch (name) {
    case 'edit':
      return (
        <svg {...props}>
          <path d="M11 2l3 3-9 9H2v-3l9-9z" stroke={color} strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
      );
    case 'duplicate':
      return (
        <svg {...props}>
          <rect x="3" y="3" width="8" height="8" rx="1" stroke={color} strokeWidth="1.4" />
          <rect x="6" y="6" width="8" height="8" rx="1" stroke={color} strokeWidth="1.4" />
        </svg>
      );
    case 'harvest':
      return (
        <svg {...props}>
          <path d="M2 7l1.5 7h9L14 7H2z" stroke={color} strokeWidth="1.4" strokeLinejoin="round" />
          <path d="M5 7c0-2 1.5-3 3-3s3 1 3 3" stroke={color} strokeWidth="1.4" />
        </svg>
      );
    case 'photo':
      return (
        <svg {...props}>
          <rect x="2" y="4" width="12" height="9" rx="1.5" stroke={color} strokeWidth="1.4" />
          <circle cx="8" cy="8.5" r="2" stroke={color} strokeWidth="1.4" />
          <circle cx="11.5" cy="6.5" r="0.5" fill={color} />
        </svg>
      );
    case 'care':
      return (
        <svg {...props}>
          <path d="M11 1c-1.5 1-2 3-1 4.5L4.5 11C3 12 3 13 4 14c1 1 2 1 3-.5L12.5 8c1.5 1 3.5.5 4.5-1" stroke={color} strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
      );
    case 'trash':
      return (
        <svg {...props}>
          <path d="M3 4h10M5 4V2h6v2M5 4l1 11h4l1-11" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'plus':
      return (
        <svg {...props}>
          <path d="M8 3v10M3 8h10" stroke={color} strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
    case 'menu':
      return (
        <svg {...props}>
          <circle cx="3" cy="8" r="1.4" fill={color} />
          <circle cx="8" cy="8" r="1.4" fill={color} />
          <circle cx="13" cy="8" r="1.4" fill={color} />
        </svg>
      );
    default:
      return null;
  }
}

// ── Przycisk akcji w BedDetail (ikona + label) ──────────────────
function BedActionButton({ icon, label, onClick, tone = 'sage', danger = false, to }) {
  const color = danger ? 'var(--t-rust)' :
                tone === 'amber' ? 'var(--t-amber)' :
                'var(--t-sage)';
  const inner = (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: 6, padding: '12px 8px',
        borderRadius: 10,
        background: 'var(--t-bg-elev)',
        border: '1px solid var(--t-line)',
        cursor: 'pointer',
        color: 'var(--t-text-dim)',
        fontFamily: 'var(--t-mono)', fontSize: 10,
        letterSpacing: '.16em', textTransform: 'uppercase',
        transition: 'all 200ms ease',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = color;
        e.currentTarget.style.color = 'var(--t-text)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'var(--t-line)';
        e.currentTarget.style.color = 'var(--t-text-dim)';
      }}
    >
      <ActionIcon name={icon} color={color} size={18} />
      {label}
    </button>
  );
  if (to) {
    return <Link to={to} style={{ flex: 1, textDecoration: 'none' }}>{inner}</Link>;
  }
  return inner;
}

// ── BedDetail panel ──────────────────────────────────────────────
function BedDetail({ bed, idx, onEdit, onDuplicate, onHarvest, onPhotos, onDelete }) {
  if (!bed) {
    return (
      <div style={{
        padding: '32px 0', textAlign: 'center',
        color: 'var(--t-text-mute)',
      }}>
        <Sprout size={48} color="var(--t-line-2)" />
        <Meta style={{ marginTop: 14 }}>Wybierz grządkę z planu →</Meta>
      </div>
    );
  }

  const stage = plantStage(bed);
  const color = bedColor(idx);
  const letter = String.fromCharCode(65 + idx);
  const totalDays = bed.expected_harvest_date && bed.planted_date
    ? Math.max(1, daysSince(bed.planted_date) + Math.abs(daysSince(bed.expected_harvest_date)))
    : null;
  const progressPct = stage.d != null && totalDays
    ? Math.min(100, Math.max(0, Math.round((stage.d / totalDays) * 100)))
    : null;

  return (
    <div>
      <Meta style={{ marginBottom: 14 }}>Wybrana grządka</Meta>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 8 }}>
        <Display size={64} style={{ color, fontWeight: 400, lineHeight: 1 }}>{letter}</Display>
        <div>
          <Display size={28} style={{ display: 'block', letterSpacing: '-.02em', lineHeight: 1 }}>
            {bed.plant_name || 'Pusty'}
          </Display>
          {bed.plant_variety && (
            <div style={{ fontSize: 12, color: 'var(--t-text-dim)', marginTop: 4 }}>
              {bed.plant_variety} · rząd {bed.row_number || idx + 1}
            </div>
          )}
        </div>
      </div>

      {/* Stan + progress */}
      {stage.d != null && (
        <div style={{
          marginTop: 22, padding: '16px 18px',
          borderRadius: 12, background: 'var(--t-bg-elev)',
          border: '1px solid var(--t-line)',
        }}>
          <Meta style={{ marginBottom: 8 }}>Stan</Meta>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14 }}>
            <Display size={28}>d{stage.d}</Display>
            <Meta color={stage.tone}>{stage.stage}</Meta>
          </div>
          {progressPct != null && (
            <>
              <div style={{
                height: 4, background: 'var(--t-line-2)',
                borderRadius: 2, overflow: 'hidden', marginBottom: 6,
              }}>
                <div style={{
                  width: `${progressPct}%`, height: '100%',
                  background: color, transition: 'width 400ms ease',
                }} />
              </div>
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                fontFamily: 'var(--t-mono)', fontSize: 10,
                letterSpacing: '.14em', color: 'var(--t-text-mute)',
                textTransform: 'uppercase',
              }}>
                <span>{formatDate(bed.planted_date, 'short')}</span>
                <span>do {formatDate(bed.expected_harvest_date, 'short')}</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Sprays - karencja warning */}
      {bed.sprays && bed.sprays.length > 0 && (
        <>
          <Meta style={{ marginTop: 20, marginBottom: 10 }}>
            Opryski · {bed.sprays.length}
          </Meta>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {bed.sprays.slice(0, 3).map(spray => {
              const safeDate = spray.safe_harvest_date;
              const safeIn = safeDate ? -daysSince(safeDate) : null;
              const isInWithdrawal = safeIn != null && safeIn > 0;
              return (
                <div key={spray.id} style={{
                  padding: '10px 12px', borderRadius: 10,
                  background: isInWithdrawal ? 'rgba(192, 106, 74, 0.08)' : 'var(--t-bg-elev)',
                  borderLeft: isInWithdrawal ? '3px solid var(--t-rust)' : '1px solid var(--t-line)',
                  fontSize: 12,
                }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    color: 'var(--t-text)', marginBottom: 2,
                  }}>
                    <span>{spray.spray_name}</span>
                    <span style={{ color: 'var(--t-text-mute)' }}>
                      {formatDate(spray.spray_date, 'short')}
                    </span>
                  </div>
                  {isInWithdrawal && (
                    <Meta color="rust">
                      Karencja jeszcze {safeIn} dni
                    </Meta>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Notatka */}
      {bed.note && (
        <div style={{
          marginTop: 20, padding: '14px 16px',
          borderRadius: 12, background: 'var(--t-bg-elev)',
          borderLeft: '3px solid var(--t-amber)',
          fontFamily: 'var(--t-display)', fontStyle: 'italic',
          fontSize: 14, lineHeight: 1.4,
          color: 'var(--t-text-dim)',
        }}>„{bed.note}”</div>
      )}

      {/* Akcje na grządce */}
      <div style={{ marginTop: 24 }}>
        <Meta style={{ marginBottom: 10 }}>Akcje</Meta>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(92px, 1fr))',
          gap: 8,
        }}>
          <BedActionButton
            icon="harvest" label="Zbiór" tone="amber"
            onClick={() => onHarvest?.(bed)}
          />
          <BedActionButton
            icon="care" label="Pielęgnacja"
            to={`/care/add?bed=${bed.id}`}
          />
          <BedActionButton
            icon="photo" label="Zdjęcia"
            onClick={() => onPhotos?.(bed)}
          />
          <BedActionButton
            icon="edit" label="Edytuj"
            onClick={() => onEdit?.(bed)}
          />
          <BedActionButton
            icon="duplicate" label="Powiel"
            onClick={() => onDuplicate?.(bed)}
          />
          <BedActionButton
            icon="trash" label="Usuń" danger
            onClick={() => onDelete?.(bed)}
          />
        </div>
      </div>
    </div>
  );
}

// ── Plots list (gdy więcej niż 1 działka) ────────────────────────
function PlotsList({ plots, currentId, onAddPlot }) {
  if (plots.length <= 1 && !onAddPlot) return null;
  return (
    <div style={{ marginBottom: 18, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      {plots.map(p => (
        <Link
          key={p.id}
          to={`/plots/${p.id}`}
          style={{ textDecoration: 'none' }}
        >
          <Chip selected={p.id === currentId}>{p.name}</Chip>
        </Link>
      ))}
      {onAddPlot && (
        <button
          type="button"
          onClick={onAddPlot}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '4px 10px', borderRadius: 999,
            background: 'transparent',
            border: '1px dashed var(--t-line-2)',
            color: 'var(--t-text-mute)',
            fontFamily: 'var(--t-mono)', fontSize: 10,
            letterSpacing: '.18em', textTransform: 'uppercase',
            cursor: 'pointer',
            transition: 'all 200ms ease',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = 'var(--t-sage)';
            e.currentTarget.style.color = 'var(--t-sage)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = 'var(--t-line-2)';
            e.currentTarget.style.color = 'var(--t-text-mute)';
          }}
        >+ Działka</button>
      )}
    </div>
  );
}

// ── RotationPanel (T6) — analiza płodozmianu dla wybranej grządki ──
function RotationPanel({ plotId, bed, plotName }) {
  const { data, loading, error } = useApi(plotId ? `/api/plots/${plotId}/rotation` : null);

  if (loading || error || !data || !data.beds) return null;

  const bedRotation = data.beds.find(b => b.bedId === bed?.id);
  if (!bedRotation || !bedRotation.analysis) return null;

  const { analysis, plantFamily, familyLabel } = bedRotation;
  const isWarning = analysis.status === 'warning';
  const hasSuggestion = analysis.suggestedNext && analysis.suggestedNext.length > 0;

  if (!isWarning && !hasSuggestion) return null;

  return (
    <div style={{
      marginTop: 20, padding: '14px 16px', borderRadius: 12,
      background: isWarning ? 'rgba(192, 106, 74, 0.06)' : 'rgba(168, 196, 158, 0.06)',
      border: `1px solid ${isWarning ? 'rgba(192, 106, 74, 0.25)' : 'rgba(168, 196, 158, 0.25)'}`,
    }}>
      <Meta style={{ marginBottom: 8 }}>
        {isWarning ? '⚠ Płodozmian' : '✓ Płodozmian'}
      </Meta>
      {plantFamily && (
        <div style={{
          fontSize: 12, color: 'var(--t-text-dim)', marginBottom: 6,
          fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
        }}>
          Rodzina: {familyLabel} ({plantFamily})
        </div>
      )}
      {isWarning && (
        <div style={{
          fontSize: 13, color: 'var(--t-text)', marginBottom: 8,
          lineHeight: 1.5,
        }}>{analysis.reason}</div>
      )}
      {hasSuggestion && (
        <div>
          <div style={{
            fontSize: 10, color: 'var(--t-text-mute)',
            fontFamily: 'var(--t-mono)', letterSpacing: '.12em',
            textTransform: 'uppercase', marginBottom: 6,
          }}>Sugerowany następca:</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {analysis.suggestedNext.map((s, i) => (
              <span key={i} style={{
                padding: '4px 10px', borderRadius: 6,
                background: 'var(--t-bg-elev)',
                border: '1px solid var(--t-line)',
                fontFamily: 'var(--t-mono)', fontSize: 11,
                color: 'var(--t-sage)',
              }}>{s.label}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Mobile content ──────────────────────────────────────────────
function MobileContent({ plots, plot, beds, selectedBedId, setSelectedBedId, view, setView, actions, onAddBed, onAddPlot, onEditPlot, onDeletePlot }) {
  const selectedBed = beds.find(b => b.id === selectedBedId);
  const selectedIdx = beds.findIndex(b => b.id === selectedBedId);

  return (
    <div className="t-stagger" style={{ maxWidth: 480, margin: '0 auto' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '24px 24px 0',
      }}>
        <Meta>{plot?.name || 'Działka'} · {beds.length} {beds.length === 1 ? 'grządka' : beds.length < 5 ? 'grządki' : 'grządek'}</Meta>
        <PlotMenu onEdit={onEditPlot} onDelete={onDeletePlot} onAddPlot={onAddPlot} />
      </div>

      <PageTitle title="Plan działki" eyebrow="Twój ogród" />

      <div style={{ padding: '0 24px 16px' }}>
        <PlotsList plots={plots} currentId={plot?.id} onAddPlot={onAddPlot} />
      </div>

      <div style={{
        padding: '0 24px 16px',
        display: 'flex', gap: 8, alignItems: 'center',
      }}>
        <Chip selected={view === 'plan'} onClick={() => setView('plan')}>Plan</Chip>
        <Chip selected={view === 'list'} onClick={() => setView('list')}>Grządki</Chip>
        <div style={{ flex: 1 }} />
        {plot?.id && (
          <Link to={`/plots/${plot.id}/wizard`} style={{ textDecoration: 'none' }}>
            <button
              type="button"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 8,
                background: 'transparent', color: 'var(--t-amber)',
                border: '1px solid var(--t-amber)', cursor: 'pointer',
                fontFamily: 'var(--t-mono)', fontSize: 11,
                letterSpacing: '.16em', textTransform: 'uppercase',
                fontWeight: 500,
              }}
            >✨ Kreator</button>
          </Link>
        )}
        <button
          type="button"
          onClick={onAddBed}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 12px', borderRadius: 8,
            background: 'var(--t-sage)', color: 'var(--t-bg)',
            border: 'none', cursor: 'pointer',
            fontFamily: 'var(--t-mono)', fontSize: 11,
            letterSpacing: '.16em', textTransform: 'uppercase',
            fontWeight: 500,
          }}
        >
          <ActionIcon name="plus" color="var(--t-bg)" size={12} />
          Grządka
        </button>
      </div>

      <div style={{ margin: '0 24px 22px' }}>
        {beds.length === 0 ? (
          <EmptyState
            illustration="bed"
            title="Tu jeszcze nie posadzono"
            description="Dodaj pierwszą grządkę — kolekcja zaczyna się od jednej rośliny."
            action={
              <Button variant="primary" onClick={onAddBed}>+ Pierwsza grządka</Button>
            }
          />
        ) : view === 'plan' ? (
          <PlotCanvas beds={beds} selectedId={selectedBedId} onSelect={setSelectedBedId} />
        ) : (
          <BedList beds={beds} selectedId={selectedBedId} onSelect={setSelectedBedId} />
        )}
      </div>

      {/* Bottom sheet style: BedDetail wjeżdża jako sticky pod listą */}
      {selectedBed && (
        <div style={{
          margin: '0 24px 22px',
          padding: '20px',
          borderRadius: 16,
          background: 'var(--t-bg-elev-2)',
          border: '1px solid var(--t-line-2)',
        }}>
          <BedDetail bed={selectedBed} idx={selectedIdx} {...actions} />
          <RotationPanel plotId={plot?.id} bed={selectedBed} />
        </div>
      )}
    </div>
  );
}

// ── Desktop content (split: canvas + BedDetail) ──────────────────
function DesktopContent({ plots, plot, beds, selectedBedId, setSelectedBedId, view, setView, withRail, actions, onAddBed, onAddPlot, onEditPlot, onDeletePlot }) {
  const selectedBed = beds.find(b => b.id === selectedBedId);
  const selectedIdx = beds.findIndex(b => b.id === selectedBedId);

  return (
    <div className="t-stagger" style={{ padding: '32px 40px 60px' }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 18,
        marginBottom: 18, paddingBottom: 18,
        borderBottom: '1px solid var(--t-line)',
      }}>
        <Display size={36} style={{ letterSpacing: '-.02em' }}>{plot?.name || 'Działka'}</Display>
        <Meta>{beds.length} {beds.length === 1 ? 'grządka' : beds.length < 5 ? 'grządki' : 'grządek'}</Meta>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <Chip selected={view === 'plan'} onClick={() => setView('plan')}>Plan</Chip>
          <Chip selected={view === 'list'} onClick={() => setView('list')}>Grządki</Chip>
          <Link to="/garden-tasks" style={{ textDecoration: 'none' }}>
            <Chip>Kwitnienie →</Chip>
          </Link>
          <Link to="/garden-tasks/seasonal" style={{ textDecoration: 'none' }}>
            <Chip>Zimowanie →</Chip>
          </Link>
          <PlotMenu onEdit={onEditPlot} onDelete={onDeletePlot} onAddPlot={onAddPlot} />
        </div>
      </div>

      <PlotsList plots={plots} currentId={plot?.id} onAddPlot={onAddPlot} />

      <div style={{
        display: 'grid',
        gridTemplateColumns: withRail ? '1fr' : '1.4fr 1fr',
        gap: 32, alignItems: 'start',
      }}>
        <div>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            marginBottom: 16,
          }}>
            <Meta>Widok z góry</Meta>
            <div style={{ display: 'flex', gap: 8 }}>
              {plot?.id && (
                <Link to={`/plots/${plot.id}/wizard`} style={{ textDecoration: 'none' }}>
                  <button
                    type="button"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '6px 12px', borderRadius: 8,
                      background: 'transparent', color: 'var(--t-amber)',
                      border: '1px solid var(--t-amber)', cursor: 'pointer',
                      fontFamily: 'var(--t-mono)', fontSize: 10,
                      letterSpacing: '.18em', textTransform: 'uppercase',
                      fontWeight: 500,
                    }}
                  >✨ Kreator obsadzania</button>
                </Link>
              )}
              <button
                type="button"
                onClick={onAddBed}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', borderRadius: 8,
                  background: 'var(--t-sage)', color: 'var(--t-bg)',
                  border: 'none', cursor: 'pointer',
                  fontFamily: 'var(--t-mono)', fontSize: 10,
                  letterSpacing: '.18em', textTransform: 'uppercase',
                  fontWeight: 500,
                }}
              >
                <ActionIcon name="plus" color="var(--t-bg)" size={12} />
                Nowa grządka
              </button>
            </div>
          </div>
          {beds.length === 0 ? (
            <EmptyState
              illustration="bed"
              title="Tu jeszcze nie posadzono"
              description="Dodaj pierwszą grządkę — kolekcja zaczyna się od jednej rośliny."
              action={
                <Button variant="primary" onClick={onAddBed}>+ Pierwsza grządka</Button>
              }
            />
          ) : view === 'plan' ? (
            <PlotCanvas beds={beds} selectedId={selectedBedId} onSelect={setSelectedBedId} big />
          ) : (
            <BedList beds={beds} selectedId={selectedBedId} onSelect={setSelectedBedId} />
          )}
        </div>

        {!withRail && <BedDetail bed={selectedBed} idx={selectedIdx} {...actions} />}
        {!withRail && selectedBed && <RotationPanel plotId={plot?.id} bed={selectedBed} />}
      </div>
    </div>
  );
}

// ── PlotMenu (3 kropki: edit/delete/add) ─────────────────────────
function PlotMenu({ onEdit, onDelete, onAddPlot }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label="Menu działki"
        style={{
          width: 30, height: 30, borderRadius: 8,
          background: open ? 'var(--t-bg-elev)' : 'transparent',
          border: '1px solid var(--t-line)',
          color: 'var(--t-text-dim)',
          cursor: 'pointer',
          display: 'grid', placeItems: 'center',
        }}
      ><ActionIcon name="menu" size={14} /></button>
      {open && (
        <>
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 10,
              background: 'transparent',
            }}
            onClick={() => setOpen(false)}
          />
          <div style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0,
            minWidth: 200,
            background: 'var(--t-bg)',
            border: '1px solid var(--t-line)',
            borderRadius: 10,
            boxShadow: '0 12px 32px rgba(0,0,0,.5)',
            padding: 6, zIndex: 11,
          }}>
            <MenuItem icon="edit" label="Edytuj działkę" onClick={() => { setOpen(false); onEdit?.(); }} />
            <MenuItem icon="plus" label="Nowa działka" onClick={() => { setOpen(false); onAddPlot?.(); }} />
            <div style={{ height: 1, background: 'var(--t-line)', margin: '4px 0' }} />
            <MenuItem icon="trash" label="Usuń działkę" danger onClick={() => { setOpen(false); onDelete?.(); }} />
          </div>
        </>
      )}
    </div>
  );
}

function MenuItem({ icon, label, onClick, danger }) {
  const color = danger ? 'var(--t-rust)' : 'var(--t-text)';
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 10px', borderRadius: 6,
        background: 'transparent', border: 'none',
        color, cursor: 'pointer', textAlign: 'left',
        fontSize: 13, fontFamily: 'var(--t-sans)',
        transition: 'background 150ms ease',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--t-bg-elev)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <ActionIcon name={icon} color={color} size={14} />
      {label}
    </button>
  );
}

// ── Plot screen root ─────────────────────────────────────────────
export default function Plot() {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isPhone, isTablet, isDesktop } = useViewport();
  const [selectedBedId, setSelectedBedId] = useState(null);
  const [view, setView] = useState('plan');

  // Modale state
  const [modal, setModal] = useState(null); // {kind, bed?, prefill?}

  // W1: dokąd wrócić po anulowaniu modalu "Posadź" (origin np. /plants/:id z PlantDetail).
  const [returnTo, setReturnTo] = useState(null);
  // Flaga: modal zamknięto PO zapisie (nie anulowaniu) → NIE wracaj do origin, zostań przy grządce.
  const savedRef = useRef(false);

  const { data: plots, loading: plotsLoading, error: plotsError, refetch: refetchPlots } = useApi('/api/plots');

  // W2: domyślna działka = pierwsza Z ZAWARTOŚCIĄ (bed_count>0), fallback: pierwsza.
  // Wcześniej brano plots[0] = najnowszą (created_at DESC) — często pustą testową → user widział
  // "Tu jeszcze nie posadzono" mimo pełnych ogrodów.
  const defaultPlotId = useMemo(() => {
    if (!plots || plots.length === 0) return undefined;
    const withBeds = plots.find(p => (p.bed_count || 0) > 0);
    return (withBeds || plots[0]).id;
  }, [plots]);

  // Auto-open modali z query params (z PlantDetail / legacy /plots/new / /plots/:id/edit)
  useEffect(() => {
    if (!plots) return;
    if (searchParams.get('add_bed') === '1' && plots.length > 0) {
      const prefill = {
        plant_name: searchParams.get('plant_name') || '',
        plant_variety: searchParams.get('plant_variety') || '',
      };
      setModal({ kind: 'bed-add', prefill });
      // W1: zapamiętaj skąd user przyszedł (baza roślin), żeby wrócić po anulowaniu.
      const from = searchParams.get('from');
      if (from) { setReturnTo(from); savedRef.current = false; }
    } else if (searchParams.get('add_plot') === '1') {
      setModal({ kind: 'plot-add' });
    } else if (searchParams.get('edit') === '1' && paramId) {
      setModal({ kind: 'plot-edit' });
    } else {
      return;
    }
    // Wyczyść query params żeby F5 nie otwierało ponownie modala
    const clean = new URLSearchParams(searchParams);
    ['add_bed','plant_id','plant_name','plant_variety','days_to_harvest','add_plot','edit','from'].forEach(k => clean.delete(k));
    setSearchParams(clean, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plots, paramId]);

  // Auto-select domyślną działkę (z zawartością) jeśli URL bez ID
  useEffect(() => {
    if (!paramId && defaultPlotId) {
      navigate(`/plots/${defaultPlotId}`, { replace: true });
    }
  }, [paramId, defaultPlotId, navigate]);

  const plotId = paramId || defaultPlotId;
  const { data: plotDetails, loading: plotLoading, refetch: refetchPlot } = useApi(
    plotId ? `/api/plots/${plotId}/details` : null,
    { deps: [plotId] }
  );

  // useMemo żeby beds nie tworzyło nowej referencji każdy render —
  // wcześniej powodowało infinite loop w useEffect poniżej (eslint warning).
  const beds = useMemo(() => plotDetails?.beds || [], [plotDetails]);
  const existingRows = useMemo(() => beds.map(b => b.row_number).filter(Boolean), [beds]);

  // Auto-select pierwszego beda gdy załadują się szczegóły
  useEffect(() => {
    if (beds.length > 0 && !selectedBedId) {
      setSelectedBedId(beds[0].id);
    }
    // Jeśli wybrana grządka zniknęła (np. po usunięciu), wybierz pierwszą lub null
    if (selectedBedId && !beds.find(b => b.id === selectedBedId)) {
      setSelectedBedId(beds[0]?.id || null);
    }
  }, [beds, selectedBedId]);

  if (plotsError) {
    return <Layout active="plot"><ErrorState error={plotsError} /></Layout>;
  }
  if (plotsLoading || (plotId && plotLoading && !plotDetails)) {
    return <Layout active="plot"><LoadingState label="Ładuję działkę..." /></Layout>;
  }
  if (!plots || plots.length === 0) {
    return (
      <Layout active="plot">
        <div style={{ maxWidth: 480, margin: '60px auto', padding: 24, textAlign: 'center' }}>
          <Sprout size={120} color="var(--t-line-2)" />
          <Display size={36} style={{
            display: 'block', marginTop: 24, marginBottom: 14,
            letterSpacing: '-.02em',
          }}>Twój ogród czeka</Display>
          <div style={{
            fontSize: 14, color: 'var(--t-text-dim)',
            lineHeight: 1.55, marginBottom: 28,
          }}>
            Działka to fizyczne miejsce w ogrodzie — może mieć wiele grządek.
            Stwórz pierwszą żeby zacząć planować uprawy.
          </div>
          <Button variant="primary" onClick={() => setModal({ kind: 'plot-add' })}>
            + Pierwsza działka
          </Button>
        </div>
        <PlotFormModal
          open={modal?.kind === 'plot-add'}
          onClose={() => setModal(null)}
          onSaved={(newPlot) => {
            refetchPlots();
            if (newPlot?.id) navigate(`/plots/${newPlot.id}`);
          }}
        />
      </Layout>
    );
  }

  const selectedBed = beds.find(b => b.id === selectedBedId);
  const selectedIdx = beds.findIndex(b => b.id === selectedBedId);

  // Wspólne callback'i dla BedDetail (edit/duplicate/harvest/photos/delete)
  const bedActions = {
    onEdit:      (bed) => setModal({ kind: 'bed-edit',      bed }),
    onDuplicate: (bed) => setModal({ kind: 'bed-duplicate', bed }),
    onHarvest:   (bed) => setModal({ kind: 'bed-harvest',   bed }),
    onPhotos:    (bed) => setModal({ kind: 'bed-photos',    bed }),
    onDelete:    (bed) => setModal({ kind: 'bed-delete',    bed }),
  };

  // Akcje na poziomie działki
  const onAddBed   = () => setModal({ kind: 'bed-add' });
  const onAddPlot  = () => setModal({ kind: 'plot-add' });
  const onEditPlot = () => setModal({ kind: 'plot-edit' });
  const onDeletePlot = () => setModal({ kind: 'plot-delete' });

  // Wspólny refresh po zapisie / usunięciu
  const refresh = () => {
    refetchPlots();
    refetchPlot?.();
  };

  // ── Render ──────────────────────────────────────────────────────
  const content = (isPhone || isTablet) ? (
    <MobileContent
      plots={plots} plot={plotDetails} beds={beds}
      selectedBedId={selectedBedId} setSelectedBedId={setSelectedBedId}
      view={view} setView={setView}
      actions={bedActions}
      onAddBed={onAddBed} onAddPlot={onAddPlot}
      onEditPlot={onEditPlot} onDeletePlot={onDeletePlot}
    />
  ) : (
    <DesktopContent
      plots={plots} plot={plotDetails} beds={beds}
      selectedBedId={selectedBedId} setSelectedBedId={setSelectedBedId}
      view={view} setView={setView}
      withRail={isDesktop}
      actions={bedActions}
      onAddBed={onAddBed} onAddPlot={onAddPlot}
      onEditPlot={onEditPlot} onDeletePlot={onDeletePlot}
    />
  );

  return (
    <>
      {(isPhone || isTablet) ? (
        <Layout active="plot">{content}</Layout>
      ) : (
        <Layout
          active="plot"
          topBar={<TopBar
            left={<Meta>{plotDetails?.name || 'Działka'} · {beds.length} {beds.length === 1 ? 'grządka' : beds.length < 5 ? 'grządki' : 'grządek'}</Meta>}
          />}
          rightRail={isDesktop ? <BedDetail bed={selectedBed} idx={selectedIdx} {...bedActions} /> : null}
        >
          {content}
        </Layout>
      )}

      {/* ── Modale CRUD ─────────────────────────────────────────── */}
      <PlotFormModal
        open={modal?.kind === 'plot-add'}
        onClose={() => setModal(null)}
        onSaved={(newPlot) => {
          refresh();
          if (newPlot?.id) navigate(`/plots/${newPlot.id}`);
        }}
      />
      <PlotFormModal
        open={modal?.kind === 'plot-edit'}
        plot={plotDetails}
        onClose={() => setModal(null)}
        onSaved={refresh}
      />
      <PlotDeleteModal
        open={modal?.kind === 'plot-delete'}
        plot={plotDetails}
        bedsCount={beds.length}
        onClose={() => setModal(null)}
        onDeleted={() => {
          refetchPlots();
          // Po usunięciu wracamy do listy / pierwszej działki
          const remaining = (plots || []).filter(p => p.id !== plotDetails.id);
          if (remaining.length > 0) {
            navigate(`/plots/${remaining[0].id}`, { replace: true });
          } else {
            navigate('/plots', { replace: true });
          }
        }}
      />
      <BedFormModal
        open={modal?.kind === 'bed-add'}
        plotId={plotId}
        existingRows={existingRows}
        prefill={modal?.prefill}
        onClose={() => {
          setModal(null);
          // W1: anulowanie (nie zapis) + przyszliśmy z bazy roślin → wróć tam.
          // Zapis woła onSaved PRZED onClose i ustawia savedRef → wtedy zostajemy przy grządce.
          if (returnTo && !savedRef.current) navigate(returnTo);
          savedRef.current = false;
          setReturnTo(null);
        }}
        onSaved={(newBed) => {
          savedRef.current = true;
          refetchPlot?.();
          if (newBed?.id) setSelectedBedId(newBed.id);
        }}
      />
      <BedFormModal
        open={modal?.kind === 'bed-edit'}
        plotId={plotId}
        bed={modal?.bed}
        existingRows={existingRows}
        onClose={() => setModal(null)}
        onSaved={refetchPlot}
      />
      <BedDuplicateModal
        open={modal?.kind === 'bed-duplicate'}
        plotId={plotId}
        bed={modal?.bed}
        existingRows={existingRows}
        onClose={() => setModal(null)}
        onSaved={(newBed) => {
          refetchPlot?.();
          if (newBed?.id) setSelectedBedId(newBed.id);
        }}
      />
      <BedHarvestModal
        open={modal?.kind === 'bed-harvest'}
        bed={modal?.bed}
        onClose={() => setModal(null)}
        onSaved={refetchPlot}
      />
      <BedDeleteModal
        open={modal?.kind === 'bed-delete'}
        bed={modal?.bed}
        onClose={() => setModal(null)}
        onDeleted={() => refetchPlot?.()}
      />
      <BedPhotosModal
        open={modal?.kind === 'bed-photos'}
        bed={modal?.bed}
        onClose={() => setModal(null)}
      />
    </>
  );
}
